/**
 * Interests Modal Component
 *
 * Curated Bumble-style picker:
 *   - Top picks (cross-category leading section, 15 items)
 *   - Strong sections (10 categories × 12 curated chips, inline "Load more")
 *   - Weak sections (header rows; tap → second sheet drilldown)
 *   - "Browse all categories" link → second sheet drilldown
 *
 * Sources curated tree from GET /v1/curated-tags. Falls back to per-tag
 * Lucide icon when tag.emoji is null (legacy tags). Search bar is global
 * by default, scopes when a section is expanded.
 *
 * @see app/Service/Web/Views/components/interests-modal.twig
 * @see app/Infrastructure/Gateway/CuratedTagGateway.php
 */

import { lockBodyScroll, unlockBodyScroll } from '../modules/scroll-lock.js';

export {
    interestsModal,
    interestsDisplay,
    searchInterestsFilter,
    interestsBrowseSheet,
    TAG_CATEGORIES,
    getCategoryInfo,
};

function t(key) {
    return window.t ? window.t(key) : '';
}

// Tag category configuration. Used by non-modal chip surfaces that don't
// call /v1/curated-tags (search filter chips, profile display). The picker
// modal uses /v1/curated-tags directly. Keep the icon + color map in sync
// with the cat_ids in tag_category.
const TAG_CATEGORIES = {
    0:  { nameKey: 'common.category_most_popular', icon: 'star',       color: 'text-amber-400' },
    10: { nameKey: 'common.category_food_drink',    icon: 'utensils',   color: 'text-orange-500' },
    11: { nameKey: 'common.category_music',         icon: 'music',      color: 'text-violet-500' },
    12: { nameKey: 'common.category_movies_tv',     icon: 'film',       color: 'text-sky-500' },
    13: { nameKey: 'common.category_fashion_beauty',icon: 'sparkles',   color: 'text-pink-400' },
    14: { nameKey: 'common.category_sports',        icon: 'trophy',     color: 'text-emerald-500' },
    15: { nameKey: 'common.category_travel',        icon: 'plane',      color: 'text-sky-500' },
    16: { nameKey: 'common.category_profession',    icon: 'briefcase',  color: 'text-slate-500' },
    17: { nameKey: 'common.category_games',         icon: 'gamepad-2',  color: 'text-indigo-500' },
    18: { nameKey: 'common.category_hobbies',       icon: 'palette',    color: 'text-rose-400' },
    19: { nameKey: 'common.category_books_culture', icon: 'book-open',  color: 'text-amber-600' },
    20: { nameKey: 'common.category_other',         icon: 'lightbulb',  color: 'text-yellow-500' },
    21: { nameKey: 'common.category_animals_pets',  icon: 'paw-print',  color: 'text-orange-500' },
    22: { nameKey: 'common.category_self_care',     icon: 'heart-pulse',color: 'text-green-500' },
    24: { nameKey: 'common.category_going_out',     icon: 'beer',       color: 'text-amber-500' },
};

Object.keys(TAG_CATEGORIES).forEach((key) => {
    const cat = TAG_CATEGORIES[key];
    Object.defineProperty(cat, 'name', {
        get() { return t(this.nameKey); },
        enumerable: true,
    });
});

const MAX_TAGS = 25;
const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 40;
const TOP_PICKS_SECTION_KEY = 'top_picks';

function getCategoryInfo(catId) {
    const id = parseInt(catId, 10);
    return TAG_CATEGORIES[id] || TAG_CATEGORIES[20];
}

/**
 * One page of /v1/search/tag. Shared by the picker's per-section loader,
 * the in-modal global search, and the browse-all second sheet.
 *
 * Callers handle their own stale-response snapshot guard, accumulate-vs-
 * replace decision, and `hasMore` math — those vary by surface. This
 * helper owns: param building, X-AUTH-TOKEN header, parse, normalize.
 *
 * Resolves to { tags, total }. `tags` is normalized via normalizeTag;
 * `total` is null when the API didn't return one (caller falls back to
 * its existing total estimate).
 *
 * Rejects with Error when the network call fails or the API returns a
 * non-2xx. The caller's try/catch handles UI error state.
 */
async function fetchTagPage({ catId = null, text = '', offset = 0, limit = PAGE_SIZE, signal = null }) {
    const token = await window.getApiToken();
    if (!token) throw new Error(t('common.session_expired_reload'));

    const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
    });
    if (catId !== null && catId !== undefined) params.set('cat_id', String(catId));
    if (text) params.set('text', text);

    const response = await fetch(`/v1/search/tag?${params}`, {
        headers: { 'X-AUTH-TOKEN': token, Accept: 'application/json' },
        credentials: 'include',
        signal,
    });
    if (!response.ok) throw new Error(t('common.interests_load_failed'));

    const data = await response.json();
    return {
        tags: (data.data || []).map(normalizeTag),
        total: typeof data.total === 'number' ? data.total : null,
    };
}

/**
 * Alpine factory for the curated interests modal.
 */
function interestsModal() {
    return {
        // ── core state ───────────────────────────────────────────────
        isOpen: false,
        isLoading: false,       // initial curated-tree fetch
        isSearchLoading: false, // /v1/search/tag global search
        isSaving: false,
        error: null,
        endpoint: '/v1/user-tag/self',
        callbackOnly: false,
        onSaveCallback: null,

        // selected tags persist across open/close (callers pass in initial list)
        selectedTags: [],
        // Mirror of selectedTags as a Set of string ids, kept in sync on every
        // mutation. Reassigned (not mutated) so Alpine reactivity tracks it,
        // giving isSelected() O(1) lookups inside x-for re-renders.
        selectedTagIds: new Set(),
        savedTags: [],          // snapshot for unsaved-changes guard
        isConfirmOpen: false,   // suppress re-entrant attemptClose() while ConfirmModal is showing

        // search
        searchOpen: false,      // collapsed by default; user opens via header icon
        searchText: '',
        searchTimeout: null,
        searchResults: [],
        searchTotal: 0,
        searchOffset: 0,
        searchHasMore: false,
        searchScopeCatId: null, // null = global; otherwise scoped to a cat
        // AbortController for the in-flight /v1/search/tag request. Aborting
        // is reactive: keystrokes during the debounce window cancel the prior
        // ES query so the backend doesn't process result sets the user has
        // already typed past, and a modal close terminates any pending fetch.
        searchAbortController: null,

        // curated tree (from /v1/curated-tags)
        topPicks: [],
        // sections is an ordered list of category objects:
        //   { id, emoji, icon, name, weight: 'strong'|'weak', curated: [...],
        //     expanded, loaded, hasMore, total, offset }
        sections: [],

        // browse-all second-sheet state managed by interestsBrowseSheet().
        // We just dispatch an event to open it.

        // ── config + DOM constants ───────────────────────────────────
        maxTags: MAX_TAGS,
        PAGE_SIZE,
        topPicksKey: TOP_PICKS_SECTION_KEY,

        // ── computed ─────────────────────────────────────────────────
        get selectedCount() { return this.selectedTags.length; },
        get canAddMore() { return this.selectedTags.length < this.maxTags; },
        get hasUnsavedChanges() {
            if (this.selectedTags.length !== this.savedTags.length) return true;
            const savedIds = new Set(this.savedTags.map((tag) => String(tag.id)));
            return this.selectedTags.some((tag) => !savedIds.has(String(tag.id)));
        },
        get isSearching() { return this.searchText.trim().length > 0; },
        get expandedSection() {
            return this.sections.find((s) => s.expanded) || null;
        },

        // ── lifecycle ────────────────────────────────────────────────
        init() {
            // ESC handling lives on the twig wrapper via @keydown.escape.window.
            // No duplicate document listener.
        },

        async open(initialTags = [], onSave = null, callbackOnly = false, endpoint = '/v1/user-tag/self') {
            // Guard against rapid double-dispatch of open-interests-modal
            // (double-click, two components requesting on the same tick).
            // Set isLoading synchronously *before* any other state mutation
            // so a second synchronous open() call sees it true and returns
            // without overwriting selectedTags/savedTags from the first
            // caller. The guard is cleared in the finally block of
            // loadCuratedTree (or here when the tree is already cached).
            if (this.isLoading) return;
            this.isLoading = true;

            try {
                // Deep-copy tag dicts via normalizeTag so savedTags and
                // selectedTags don't share object references. Future in-place
                // edits to a tag in selectedTags would otherwise silently
                // mutate savedTags and defeat the unsaved-changes diff.
                this.selectedTags = initialTags.map(normalizeTag);
                this.selectedTagIds = new Set(this.selectedTags.map((tag) => String(tag.id)));
                this.savedTags = initialTags.map(normalizeTag);
                this.onSaveCallback = onSave;
                this.callbackOnly = callbackOnly;
                this.endpoint = endpoint || '/v1/user-tag/self';
                this.searchOpen = false;
                this.searchText = '';
                this.searchResults = [];
                this.searchTotal = 0;
                this.searchOffset = 0;
                this.searchHasMore = false;
                this.searchScopeCatId = null;
                this.error = null;
                this.isOpen = true;

                lockBodyScroll();

                if (this.sections.length === 0 && this.topPicks.length === 0) {
                    await this.loadCuratedTreeInner();
                } else {
                    // Reset any previously-expanded section state on re-open.
                    this.collapseAllSections();
                }
            } finally {
                this.isLoading = false;
                this.$nextTick(() => {
                    this.refreshIcons();
                });
            }
        },

        attemptClose() {
            // Suppress re-entrant calls: if the backdrop is clicked again
            // while the discard-changes confirm is already open, do nothing.
            if (this.isConfirmOpen) return;

            if (this.hasUnsavedChanges && window.ConfirmModal) {
                this.isConfirmOpen = true;
                window.ConfirmModal.show({
                    title: t('common.discard_changes'),
                    confirmText: t('common.discard'),
                    cancelText: t('common.keep_editing'),
                    variant: 'danger',
                    onConfirm: () => {
                        this.isConfirmOpen = false;
                        this.close();
                    },
                    onCancel: () => {
                        this.isConfirmOpen = false;
                    },
                });
                return;
            }
            this.close();
        },

        close() {
            this.isOpen = false;
            unlockBodyScroll();
            // Cancel any in-flight search before tearing down state so the
            // response doesn't land and repopulate searchResults on a closed
            // modal.
            clearTimeout(this.searchTimeout);
            this.abortInflightSearch();
            // Clear transient state only — keep selectedTags so the caller's
            // ref isn't wiped before save bubbles back through the callback.
            this.searchOpen = false;
            this.searchText = '';
            this.searchResults = [];
            this.isSearchLoading = false;
        },

        // ── curated tree loading ─────────────────────────────────────
        // Public entry point — sets isLoading for the error/retry path in
        // the template. open() uses loadCuratedTreeInner() directly because
        // it manages isLoading itself for the open() concurrency guard.
        async loadCuratedTree() {
            if (this.isLoading) return;
            this.isLoading = true;
            try {
                await this.loadCuratedTreeInner();
            } finally {
                this.isLoading = false;
                this.refreshIcons();
            }
        },

        async loadCuratedTreeInner() {
            this.error = null;
            try {
                const response = await fetch('/v1/curated-tags', {
                    headers: { Accept: 'application/json' },
                    credentials: 'include',
                });
                if (!response.ok) {
                    throw new Error(t('common.interests_load_failed'));
                }
                const tree = await response.json();
                this.topPicks = (tree.top_picks || []).map(normalizeTag);
                this.sections = (tree.categories || []).map((cat) => {
                    const curated = (cat.tags || []).map(normalizeTag);
                    const total = typeof cat.total === 'number' ? cat.total : 0;
                    return {
                        id: cat.id,
                        emoji: cat.emoji,
                        icon: (TAG_CATEGORIES[cat.id] || {}).icon || 'tag',
                        nameKey: (TAG_CATEGORIES[cat.id] || {}).nameKey,
                        weight: curated.length > 0 ? 'strong' : 'weak',
                        curated,
                        expanded: false,
                        loaded: false,
                        loading: false,
                        // Whether expanding can possibly reveal anything beyond
                        // the curated chips. Drives the "Load more in {cat}"
                        // affordance. Falls back to true when total is unknown
                        // (older API responses) so the button still appears.
                        hasMoreBeyondCurated: total === 0 || total > curated.length,
                        hasMore: false,
                        total,
                        offset: 0,
                        extraTags: [],
                    };
                });
            } catch (err) {
                console.error('Error loading curated tree:', err);
                this.error = err.message || t('common.interests_load_failed');
            }
        },

        sectionName(section) {
            if (section.nameKey) return t(section.nameKey);
            return '';
        },

        // ── selection ────────────────────────────────────────────────
        isSelected(tagId) {
            return this.selectedTagIds.has(String(tagId));
        },

        toggleTag(tag) {
            const id = String(tag.id);
            const nextIds = new Set(this.selectedTagIds);
            if (nextIds.has(id)) {
                this.selectedTags = this.selectedTags.filter((t) => String(t.id) !== id);
                nextIds.delete(id);
            } else if (this.canAddMore) {
                this.selectedTags = [...this.selectedTags, normalizeTag(tag)];
                nextIds.add(id);
            }
            this.selectedTagIds = nextIds;
        },

        getCategoryInfo(catId) { return getCategoryInfo(catId); },

        // ── pinning: surface user's selected tags at the top of their section ─
        sectionTags(section) {
            // Build [pinned selected from this cat that aren't curated] + curated.
            const curatedIds = new Set(section.curated.map((tag) => String(tag.id)));
            const pinned = this.selectedTags
                .filter((tag) => parseInt(tag.cat_id, 10) === section.id
                    && !curatedIds.has(String(tag.id)));
            // Build a Set of pinned ids once; filter against it in O(1).
            const pinnedIds = new Set(pinned.map((tag) => String(tag.id)));
            const expanded = section.expanded ? section.extraTags : [];
            const expandedFiltered = expanded.filter(
                (tag) => !curatedIds.has(String(tag.id)) && !pinnedIds.has(String(tag.id)),
            );
            return [...pinned, ...section.curated, ...expandedFiltered];
        },

        // ── per-section "Load more" ─────────────────────────────────
        async expandSection(section) {
            // Guard rapid double-clicks: section.loading is set in
            // loadSectionPage but only *after* an await, so a second
            // synchronous click can pass the !section.loaded check before
            // the first call sets it. Set the flag here, synchronously,
            // before any await.
            if (section.loading) return;
            if (!section.expanded) {
                section.expanded = true;
            }
            if (!section.loaded) {
                section.offset = 0;
                section.extraTags = [];
                section.loading = true; // synchronous guard
                try {
                    await this.loadSectionPage(section);
                    section.loaded = true;
                } finally {
                    // loadSectionPage's own finally sets section.loading = false,
                    // but if we set it here we'd double-clear; rely on it.
                }
            }
            this.refreshIcons();
        },

        async loadSectionPage(section) {
            // Per-section flag — keeps the search "Load more" affordances on
            // other sections visible while this one is loading.
            section.loading = true;
            this.error = null;
            try {
                const { tags: newTags, total } = await fetchTagPage({
                    catId: section.id,
                    offset: section.offset,
                });

                if (section.offset === 0) {
                    section.extraTags = newTags;
                } else {
                    section.extraTags = [...section.extraTags, ...newTags];
                }
                if (total !== null) section.total = total;
                section.offset += newTags.length;
                section.hasMore = newTags.length === PAGE_SIZE
                    && (section.total === 0 || section.offset < section.total);
            } catch (err) {
                console.error('Error loading section:', err);
                this.error = err.message || t('common.interests_load_failed');
            } finally {
                section.loading = false;
                this.refreshIcons();
            }
        },

        async loadMoreInSection(section) {
            if (!section.expanded) {
                await this.expandSection(section);
                return;
            }
            if (section.loading || !section.hasMore) return;
            await this.loadSectionPage(section);
        },

        collapseSection(section) {
            section.expanded = false;
            this.refreshIcons();
        },

        collapseAllSections() {
            this.sections.forEach((s) => { s.expanded = false; });
        },

        // ── search ───────────────────────────────────────────────────
        onSearchInput() {
            clearTimeout(this.searchTimeout);
            // Any keystroke supersedes the in-flight request — cancel it so
            // the backend doesn't continue scoring a query the user has
            // already typed past.
            this.abortInflightSearch();
            if (this.searchText.trim().length === 0) {
                this.searchOffset = 0;
                this.searchTotal = 0;
                this.searchResults = [];
                this.searchHasMore = false;
                this.searchScopeCatId = null;
                this.isSearchLoading = false;
                return;
            }
            // Flip the loading flag synchronously so the spinner appears
            // during the debounce window, not just once the fetch fires.
            this.isSearchLoading = true;
            this.searchTimeout = setTimeout(() => {
                this.searchOffset = 0;
                this.searchTotal = 0;
                this.searchResults = [];
                this.searchHasMore = false;
                // Scope by the expanded section, if any.
                this.searchScopeCatId = this.expandedSection
                    ? this.expandedSection.id
                    : null;
                this.runSearchPage();
            }, SEARCH_DEBOUNCE_MS);
        },

        abortInflightSearch() {
            if (this.searchAbortController) {
                this.searchAbortController.abort();
                this.searchAbortController = null;
            }
        },

        async runSearchPage() {
            this.isSearchLoading = true;
            this.error = null;
            // Snapshot the scope and search text at request fire. If either
            // changes before the response lands (user collapsed a section or
            // cleared the search), discard the response so the UI doesn't
            // show stale results.
            const requestScope = this.searchScopeCatId;
            const requestText = this.searchText.trim();
            const controller = new AbortController();
            this.searchAbortController = controller;
            try {
                const { tags: newTags, total } = await fetchTagPage({
                    catId: requestScope,
                    text: requestText,
                    offset: this.searchOffset,
                    signal: controller.signal,
                });

                // Drop the response if the user has since changed scope or
                // text — the in-flight result no longer matches the UI.
                if (this.searchScopeCatId !== requestScope || this.searchText.trim() !== requestText) {
                    return;
                }

                if (this.searchOffset === 0) {
                    this.searchResults = newTags;
                } else {
                    this.searchResults = [...this.searchResults, ...newTags];
                }
                if (total !== null) this.searchTotal = total;
                this.searchOffset += newTags.length;
                this.searchHasMore = newTags.length === PAGE_SIZE
                    && (this.searchTotal === 0 || this.searchOffset < this.searchTotal);
            } catch (err) {
                // Aborts are deliberate (newer keystroke or modal close) —
                // surfacing them as errors would flash a spurious banner.
                if (err.name === 'AbortError') return;
                console.error('Error searching tags:', err);
                this.error = err.message || t('common.interests_load_failed');
            } finally {
                if (this.searchAbortController === controller) {
                    this.searchAbortController = null;
                }
                this.isSearchLoading = false;
                this.refreshIcons();
            }
        },

        loadMoreSearch() {
            if (!this.isSearchLoading && this.searchHasMore) this.runSearchPage();
        },

        toggleSearch() {
            if (this.searchOpen) {
                this.closeSearch();
            } else {
                this.searchOpen = true;
                this.$nextTick(() => {
                    const input = this.$refs?.searchInput;
                    if (input) input.focus();
                    this.refreshIcons();
                });
            }
        },

        closeSearch() {
            clearTimeout(this.searchTimeout);
            this.abortInflightSearch();
            this.searchOpen = false;
            this.searchText = '';
            this.searchResults = [];
            this.searchTotal = 0;
            this.searchOffset = 0;
            this.searchHasMore = false;
            this.searchScopeCatId = null;
            this.isSearchLoading = false;
            this.$nextTick(() => this.refreshIcons());
        },

        clearSearch() {
            clearTimeout(this.searchTimeout);
            this.abortInflightSearch();
            this.searchText = '';
            this.searchResults = [];
            this.searchTotal = 0;
            this.searchOffset = 0;
            this.searchHasMore = false;
            this.searchScopeCatId = null;
            this.isSearchLoading = false;
            this.$nextTick(() => {
                const input = this.$refs?.searchInput;
                if (input) input.focus();
                this.refreshIcons();
            });
        },

        // ── browse-all second sheet ──────────────────────────────────
        openBrowseAll(catId = null) {
            window.dispatchEvent(new CustomEvent('open-interests-browse-sheet', {
                detail: {
                    initialCategory: catId,
                    sections: this.sections.map((s) => ({
                        id: s.id,
                        icon: s.icon,
                        emoji: s.emoji,
                        nameKey: s.nameKey,
                    })),
                    maxTags: this.maxTags,
                    isSelected: (id) => this.isSelected(id),
                    toggleTag: (tag) => this.toggleTag(tag),
                    // Snapshot the parent's selection state. Called from the
                    // browse-sheet after each toggle so it can refresh its
                    // own reactive view of canAddMore.
                    getSelectionState: () => ({
                        count: this.selectedTags.length,
                        canAddMore: this.canAddMore,
                    }),
                },
            }));
        },

        // ── icons ────────────────────────────────────────────────────
        // Scope Lucide re-init to this modal's subtree. document.body would
        // walk the entire page on every chip toggle — measurably expensive
        // with ~120 chips visible. $nextTick yields to Alpine's render cycle
        // (the previous setTimeout(50) was a race against rendering).
        refreshIcons() {
            this.$nextTick(() => {
                if (window.LucideIcons && this.$el) {
                    window.LucideIcons.initialize(this.$el);
                }
            });
        },

        // ── save ─────────────────────────────────────────────────────
        async save() {
            if (this.isSaving) return;
            this.isSaving = true;
            this.error = null;
            try {
                if (this.callbackOnly) {
                    if (this.onSaveCallback) this.onSaveCallback(this.selectedTags);
                    this.savedTags = [...this.selectedTags];
                    this.isSaving = false;
                    this.close();
                    return;
                }
                const token = await window.getApiToken();
                if (!token) throw new Error(t('common.session_expired_reload'));

                const tagIds = this.selectedTags.map((tag) => tag.id).join(',');
                const response = await fetch(this.endpoint, {
                    method: 'PUT',
                    headers: {
                        'X-AUTH-TOKEN': token,
                        'Content-Type': 'application/x-www-form-urlencoded',
                        Accept: 'application/json',
                    },
                    credentials: 'include',
                    body: `tag_ids=${encodeURIComponent(tagIds)}`,
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.message || t('common.interests_save_failed'));
                }
                const data = await response.json();
                if (data.data) {
                    this.selectedTags = data.data.map(normalizeTag);
                    this.selectedTagIds = new Set(this.selectedTags.map((tag) => String(tag.id)));
                }
                this.savedTags = [...this.selectedTags];
                if (this.onSaveCallback) this.onSaveCallback(this.selectedTags);
                window.dispatchEvent(new CustomEvent('interests-updated', {
                    detail: { tags: this.selectedTags },
                }));
                this.close();
            } catch (err) {
                console.error('Error saving interests:', err);
                this.error = err.message || t('common.interests_save_failed');
            } finally {
                this.isSaving = false;
            }
        },
    };
}

/**
 * Normalize a tag dict from any of: /v1/curated-tags, /v1/search/tag,
 * /v1/tag, or hand-built. Always returns { id: string, name, cat_id, emoji }.
 */
function normalizeTag(tag) {
    return {
        id: String(tag.id),
        name: tag.name,
        cat_id: parseInt(tag.cat_id, 10),
        emoji: tag.emoji ?? null,
    };
}

/**
 * Alpine component for the profile display block.
 */
function interestsDisplay() {
    return {
        tags: [],
        isEditable: false,
        isRemoving: false,
        endpoint: '/v1/user-tag/self',

        init() {
            try {
                const tagsData = this.$el.dataset.tags;
                if (tagsData) this.tags = JSON.parse(tagsData);
            } catch (e) {
                console.error('Failed to parse tags data:', e);
            }
            this.isEditable = this.$el.dataset.editable === 'true';
            this.endpoint = this.$el.dataset.endpoint || this.endpoint;
            window.addEventListener('interests-updated', (e) => {
                if (e.detail?.tags) {
                    this.tags = e.detail.tags;
                    this.refreshIcons();
                }
            });
            this.$nextTick(() => this.refreshIcons());
        },

        refreshIcons() {
            this.$nextTick(() => {
                if (window.LucideIcons) window.LucideIcons.initialize(this.$el);
            });
        },

        getCategoryInfo(catId) { return getCategoryInfo(catId); },

        async removeTag(tagId) {
            if (this.isRemoving) return;
            const removedIndex = this.tags.findIndex((t) => String(t.id) === String(tagId));
            if (removedIndex === -1) return;
            const removedTag = this.tags[removedIndex];
            this.tags.splice(removedIndex, 1);
            this.isRemoving = true;
            try {
                const token = await window.getApiToken();
                if (!token) throw new Error('Authentication required');
                const tagIds = this.tags.map((t) => t.id).join(',');
                const response = await fetch(this.endpoint, {
                    method: 'PUT',
                    headers: {
                        'X-AUTH-TOKEN': token,
                        'Content-Type': 'application/x-www-form-urlencoded',
                        Accept: 'application/json',
                    },
                    credentials: 'include',
                    body: `tag_ids=${encodeURIComponent(tagIds)}`,
                });
                if (!response.ok) throw new Error('Failed to remove interest');
                window.dispatchEvent(new CustomEvent('interests-updated', {
                    detail: { tags: this.tags },
                }));
            } catch (err) {
                console.error('Error removing tag:', err);
                this.tags.splice(removedIndex, 0, removedTag);
            } finally {
                this.isRemoving = false;
                this.refreshIcons();
            }
        },

        openEditModal() {
            window.dispatchEvent(new CustomEvent('open-interests-modal', {
                detail: {
                    tags: this.tags,
                    endpoint: this.endpoint,
                    onSave: (newTags) => {
                        this.tags = newTags;
                        this.refreshIcons();
                    },
                },
            }));
        },
    };
}

/**
 * Alpine component for the search-page filter interest selector.
 * Mirrors the same modal in callbackOnly mode.
 */
function searchInterestsFilter() {
    return {
        selectedTags: [],
        suggestions: [],

        get filteredSuggestions() {
            const selectedIds = new Set(this.selectedTags.map((t) => String(t.id)));
            return this.suggestions
                .filter((s) => !selectedIds.has(String(s.id)))
                .slice(0, 8);
        },

        addTag(tag) {
            if (!this.selectedTags.some((t) => String(t.id) === String(tag.id))) {
                this.selectedTags.push(normalizeTag(tag));
            }
        },

        init() {
            const form = this.$el.closest('form');
            if (form) {
                const tagInputs = form.querySelectorAll('input[name="tags[]"]');
                const tagIds = Array.from(tagInputs).map((input) => input.value);
                if (tagIds.length > 0) this.loadTagDetails(tagIds);
            }
            window.addEventListener('interests-updated', (e) => {
                if (e.detail?.tags) this.selectedTags = e.detail.tags;
            });
            window.addEventListener('search-interests-updated', (e) => {
                if (e.detail?.tags) this.selectedTags = e.detail.tags;
            });
        },

        async loadTagDetails(tagIds) {
            if (!tagIds.length) return;
            try {
                const token = await window.getApiToken();
                if (!token) return;
                const response = await fetch(`/v1/tag/${tagIds.join(',')}`, {
                    headers: { 'X-AUTH-TOKEN': token, Accept: 'application/json' },
                    credentials: 'include',
                });
                if (response.ok) {
                    const data = await response.json();
                    const tags = data.data || (Array.isArray(data) ? data : [data]);
                    this.selectedTags = tags.map(normalizeTag);
                }
            } catch (err) {
                console.error('Failed to load tag details:', err);
            }
        },

        getCategoryInfo(catId) { return getCategoryInfo(catId); },

        removeTag(tagId) {
            const idx = this.selectedTags.findIndex((t) => String(t.id) === String(tagId));
            if (idx > -1) this.selectedTags.splice(idx, 1);
        },

        openModal() {
            window.dispatchEvent(new CustomEvent('open-interests-modal', {
                detail: {
                    tags: this.selectedTags,
                    callbackOnly: true,
                    onSave: (newTags) => {
                        this.selectedTags = newTags;
                        window.dispatchEvent(new CustomEvent('search-interests-updated', {
                            detail: { tags: newTags },
                        }));
                    },
                },
            }));
        },
    };
}

/**
 * Alpine component for the "Browse all categories" second sheet. Opens
 * over the underlying modal — its dismiss returns to the modal with
 * state intact. Tap a category to scope into its tags; tap a chip to
 * toggle selection on the underlying modal's selectedTags via the
 * passed-through callback.
 */
function interestsBrowseSheet() {
    return {
        isOpen: false,
        activeCategory: null,
        sections: [],
        tags: [],
        isLoading: false,
        error: null,
        offset: 0,
        total: 0,
        hasMore: false,
        searchText: '',
        searchTimeout: null,
        // AbortController for /v1/search/tag — cancelled on keystroke,
        // category switch, back navigation, or close, so the backend
        // doesn't keep working on a query the user has already moved past.
        searchAbortController: null,
        toggleCallback: null,
        isSelectedCallback: null,
        getSelectionStateCallback: null,
        // Local mirror of the parent modal's selection state, refreshed after
        // each toggle so :disabled stays reactive on this component's chips
        // (Alpine doesn't track reactive reads across components).
        canAddMore: true,
        maxTags: 25,

        init() {
            window.addEventListener('open-interests-browse-sheet', (e) => {
                this.openFromEvent(e.detail || {});
            });
        },

        openFromEvent(detail) {
            this.sections = detail.sections || [];
            this.toggleCallback = detail.toggleTag || null;
            this.isSelectedCallback = detail.isSelected || (() => false);
            this.getSelectionStateCallback = detail.getSelectionState || null;
            this.maxTags = typeof detail.maxTags === 'number' ? detail.maxTags : 25;
            this.activeCategory = null;
            this.tags = [];
            this.searchText = '';
            this.offset = 0;
            this.total = 0;
            this.hasMore = false;
            this.error = null;
            this.isOpen = true;
            this.refreshSelectionState();

            if (detail.initialCategory) {
                this.selectCategory(detail.initialCategory);
            }
            this.$nextTick(() => this.refreshIcons());
        },

        refreshSelectionState() {
            if (this.getSelectionStateCallback) {
                const state = this.getSelectionStateCallback();
                this.canAddMore = state.canAddMore;
            }
        },

        close() {
            this.isOpen = false;
            this.activeCategory = null;
            clearTimeout(this.searchTimeout);
            this.abortInflightSearch();
            this.isLoading = false;
        },

        abortInflightSearch() {
            if (this.searchAbortController) {
                this.searchAbortController.abort();
                this.searchAbortController = null;
            }
        },

        sectionName(section) {
            if (!section) return '';
            return section.nameKey ? t(section.nameKey) : '';
        },

        getCategoryInfo(catId) { return getCategoryInfo(catId); },

        isSelected(tagId) {
            return this.isSelectedCallback ? this.isSelectedCallback(tagId) : false;
        },

        toggleTag(tag) {
            if (this.toggleCallback) this.toggleCallback(tag);
            this.refreshSelectionState();
            this.refreshIcons();
        },

        async selectCategory(catId) {
            this.abortInflightSearch();
            this.activeCategory = parseInt(catId, 10);
            this.tags = [];
            this.offset = 0;
            this.total = 0;
            this.hasMore = false;
            this.searchText = '';
            await this.loadPage();
        },

        async loadPage() {
            // Guard against null activeCategory (race: search input fires
            // before category selection or after backToCategories()).
            if (this.activeCategory === null) return;

            // Snapshot the request scope so a slow response that arrives
            // after the user switched categories or cleared the search is
            // dropped on the floor rather than stamped over fresh UI state.
            const requestCategory = this.activeCategory;
            const requestText = this.searchText.trim();
            const requestOffset = this.offset;
            const controller = new AbortController();
            this.searchAbortController = controller;

            this.isLoading = true;
            this.error = null;
            try {
                const { tags: newTags, total } = await fetchTagPage({
                    catId: requestCategory,
                    text: requestText,
                    offset: requestOffset,
                    signal: controller.signal,
                });

                // Drop the response if the user has since changed scope or
                // text — the in-flight result no longer matches the UI.
                if (this.activeCategory !== requestCategory || this.searchText.trim() !== requestText) {
                    return;
                }

                this.tags = requestOffset === 0 ? newTags : [...this.tags, ...newTags];
                if (total !== null) this.total = total;
                this.offset = requestOffset + newTags.length;
                this.hasMore = newTags.length === PAGE_SIZE
                    && (this.total === 0 || this.offset < this.total);
            } catch (err) {
                if (err.name === 'AbortError') return;
                this.error = err.message || t('common.interests_load_failed');
            } finally {
                if (this.searchAbortController === controller) {
                    this.searchAbortController = null;
                }
                this.isLoading = false;
                this.refreshIcons();
            }
        },

        loadMore() { if (!this.isLoading && this.hasMore) this.loadPage(); },

        backToCategories() {
            clearTimeout(this.searchTimeout);
            this.abortInflightSearch();
            this.activeCategory = null;
            this.tags = [];
            this.searchText = '';
            this.refreshIcons();
        },

        onSearchInput() {
            clearTimeout(this.searchTimeout);
            this.abortInflightSearch();
            this.searchTimeout = setTimeout(() => {
                this.offset = 0;
                this.total = 0;
                if (this.activeCategory !== null) {
                    this.loadPage();
                }
            }, SEARCH_DEBOUNCE_MS);
        },

        refreshIcons() {
            this.$nextTick(() => {
                if (window.LucideIcons && this.$el) {
                    window.LucideIcons.initialize(this.$el);
                }
            });
        },
    };
}

// Expose globally for inline scripts that bind to window.TAG_CATEGORIES.
window.TAG_CATEGORIES = TAG_CATEGORIES;
window.getCategoryInfo = getCategoryInfo;
